import { useQuery } from "@tanstack/react-query";
import type { StoredEvent } from "@facade/dashboard-shared";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { EventDetail } from "../components/EventDetail";
import { FeedbackForm } from "../components/FeedbackForm";
import { RunFeedback } from "../components/RunFeedback";
import { KindChips, nsColor } from "../components/EventStream";
import { ModelQueryDrawer } from "../components/ModelQueryDrawer";
import { Shot } from "../components/Shot";
import { api } from "../lib/api";
import { fmtCost, fmtDateTime, fmtMs, fmtNum, fmtTime, shotRef, summarize } from "../lib/format";
import { mergeEvents, useLive } from "../lib/ws";
import { kindNamespace } from "@facade/dashboard-shared";

function TimelineItem({ e, onOpenQuery, defaultOpen }: { e: StoredEvent; onOpenQuery: (e: StoredEvent) => void; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const p = e.payload as any;
  const inlineShot = e.kind === "screen.shot" ? shotRef(p) : null;
  return (
    <li style={{ ["--ns-color" as any]: nsColor(e.kind) }}>
      <div className="head" onClick={() => setOpen(!open)}>
        <span className="t">{fmtTime(e.ts)}</span>
        <span className="kind">{e.kind}</span>
        <span style={{ flex: 1, minWidth: 0 }}>{summarize(e.kind, p)}</span>
        {e.kind === "model.query" && (
          <button
            className="small primary"
            onClick={(ev) => {
              ev.stopPropagation();
              onOpenQuery(e);
            }}
          >
            open
          </button>
        )}
      </div>
      {inlineShot && !open && (
        <div className="body">
          <Shot sha={inlineShot.sha} />
        </div>
      )}
      {open && (
        <div className="body">
          <EventDetail e={e} onOpenQuery={onOpenQuery} />
        </div>
      )}
    </li>
  );
}

export function RunInspector() {
  const { deviceId = "", runId = "" } = useParams();
  const run = useQuery({ queryKey: ["run", runId], queryFn: () => api.run(runId), refetchInterval: (q) => (q.state.data?.ended_ts ? false : 5_000) });
  const hist = useQuery({ queryKey: ["run-events", runId], queryFn: () => api.runEvents(runId), refetchInterval: (q) => (run.data?.ended_ts ? false : 5_000) });
  const live = useLive({ deviceId, runId }, { limit: 1000 });
  const events = useMemo(() => mergeEvents(hist.data ?? [], live), [hist.data, live]);
  const [ns, setNs] = useState<Set<string>>(new Set());
  const shown = useMemo(() => (ns.size ? events.filter((e) => ns.has(kindNamespace(e.kind))) : events), [events, ns]);
  const queries = useMemo(() => events.filter((e) => e.kind === "model.query"), [events]);
  const [qi, setQi] = useState<number | null>(null);
  const r = run.data;
  const openQuery = (e: StoredEvent) => setQi(queries.findIndex((q) => q.id === e.id));
  const duration = r?.started_ts ? (r.ended_ts ?? Date.now()) - r.started_ts : null;

  return (
    <div className="stack">
      <RunFeedback runId={runId} deviceId={deviceId} prompt={run.data?.prompt} />
      <div className="muted" style={{ fontSize: 12 }}>
        <Link to={`/d/${deviceId}`}>← device</Link>
        {r?.parent_run_id && (
          <>
            {" · parent "}
            {r.parent_run_id.startsWith("loop:") ? <Link to={`/d/${deviceId}/feeds`}>{r.parent_run_id}</Link> : <Link to={`/d/${deviceId}/runs/${encodeURIComponent(r.parent_run_id)}`}>{r.parent_run_id}</Link>}
          </>
        )}
      </div>
      <div className="panel">
        <div className="row">
          {r ? r.ended_ts == null ? <span className="badge running">running</span> : r.ok ? <span className="badge ok">ok</span> : <span className="badge fail">failed</span> : null}
          <span className="badge">{r?.mode ?? "?"}</span>
          {r?.source && <span className="muted">{r.source}</span>}
          <span className="muted mono" style={{ fontSize: 11 }}>{runId}</span>
        </div>
        <h1 style={{ margin: "8px 0" }}>{r?.prompt ? JSON.stringify(r.prompt) : <span className="muted">(no prompt)</span>}</h1>
        {(r?.result || r?.error) && <div style={{ color: r?.error ? "var(--bad)" : undefined }}>{r?.error ?? r?.result}</div>}
        <div className="stats" style={{ marginTop: 10 }}>
          <span className="stat"><span className="v">{fmtMs(duration)}</span><span className="l">duration</span></span>
          <span className="stat"><span className="v">{fmtNum(r?.model_calls)}</span><span className="l">model calls</span></span>
          <span className="stat"><span className="v">{fmtNum(r?.tokens_in)}</span><span className="l">tokens in</span></span>
          <span className="stat"><span className="v">{fmtNum(r?.tokens_out)}</span><span className="l">tokens out</span></span>
          <span className="stat"><span className="v">{fmtCost(r?.cost_usd)}</span><span className="l">cost</span></span>
          <span className="stat"><span className="v">{events.length}</span><span className="l">events</span></span>
          <span className="stat"><span className="v" style={{ fontSize: 13 }}>{fmtDateTime(r?.started_ts)}</span><span className="l">started</span></span>
        </div>
        {r?.children && r.children.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <h3>child runs</h3>
            {r.children.map((c) => (
              <div key={c.run_id}>
                <Link to={`/d/${deviceId}/runs/${encodeURIComponent(c.run_id)}`}>{c.mode} {c.prompt ?? c.run_id}</Link> {c.ok ? <span className="badge ok">ok</span> : c.ended_ts ? <span className="badge fail">fail</span> : null}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="panel">
        <div className="toolbar">
          <KindChips value={ns} onChange={setNs} />
          <span className="muted" style={{ marginLeft: "auto", fontSize: 12 }}>
            {shown.length} events · {queries.length} model queries
          </span>
        </div>
        {shown.length === 0 ? (
          <div className="empty">{hist.isLoading ? "loading…" : "no events for this run"}</div>
        ) : (
          <ul className="tl">
            {shown.map((e) => (
              <TimelineItem key={`${e.deviceId}|${e.seq}`} e={e} onOpenQuery={openQuery} defaultOpen={e.kind === "run.end" && (e.payload as any)?.ok === false} />
            ))}
          </ul>
        )}
      </div>
      {qi != null && queries[qi] && (
        <ModelQueryDrawer
          e={queries[qi]}
          onClose={() => setQi(null)}
          onPrev={qi > 0 ? () => setQi(qi - 1) : undefined}
          onNext={qi < queries.length - 1 ? () => setQi(qi + 1) : undefined}
        />
      )}
    </div>
  );
}
